import React from "react";
import API_BASE from "../../api";
import GroupHomeView from "../../components/Groups/GroupHomeView";
import {
  formatGroupPostDate,
  getGroupCoverScale,
  GroupActionButtons,
  GroupInvitePanel,
  GroupOverviewStats,
  GroupPostMedia,
  GroupStatusBadges,
  resolveGroupCoverObjectPosition,
  resolveGroupCoverUrl,
} from "./GroupsSharedUi";

export default function GroupHomeSection({ controller, t }) {
  const { activeGroupHome } = controller;
  if (!activeGroupHome) return null;

  const composer = controller.isGroupPostComposerOpen ? (
    <form
      ref={controller.groupPostComposerRef}
      className="groups-post-composer"
      onSubmit={controller.handleCreateGroupPost}
    >
      <label htmlFor="group-post-caption" className="groups-post-composer-label">
        {t("groups_post_caption")}
      </label>
      <textarea
        id="group-post-caption"
        value={controller.groupPostCaption}
        onChange={(event) => controller.setGroupPostCaption(event.target.value)}
        maxLength={4000}
        placeholder={t("groups_post_caption_placeholder")}
      />
      <input
        ref={controller.groupPostMediaInputRef}
        type="file"
        accept="image/*,video/*"
        className="groups-cover-input"
        multiple
        onChange={controller.handleGroupPostMediaChange}
      />
      <div className="groups-post-composer-media-actions">
        <button
          type="button"
          className="groups-secondary-btn"
          onClick={() => controller.groupPostMediaInputRef.current?.click()}
          disabled={controller.groupPostSubmitting}
        >
          {t("groups_post_add_media", {
            defaultValue: "Add photo or video",
          })}
        </button>
        {controller.groupPostFiles.length > 0 ? (
          <button
            type="button"
            className="groups-secondary-btn"
            onClick={controller.clearGroupPostMediaSelection}
            disabled={controller.groupPostSubmitting}
          >
            {t("groups_post_clear_media", {
              defaultValue: "Remove all media",
            })}
          </button>
        ) : null}
      </div>
      {controller.groupPostMediaPreviews.length > 0 ? (
        <div className="groups-post-preview-grid">
          {controller.groupPostMediaPreviews.map((preview, index) => (
            <div key={preview.id} className="groups-post-preview-card">
              {preview.mediaType === "video" ? (
                <video
                  src={preview.url}
                  controls
                  className="groups-post-preview-media"
                />
              ) : (
                <img
                  src={preview.url}
                  alt={preview.name}
                  className="groups-post-preview-media"
                />
              )}
              <button
                type="button"
                className="groups-post-preview-remove"
                onClick={() => controller.removeGroupPostMedia(index)}
                disabled={controller.groupPostSubmitting}
              >
                {t("remove", { defaultValue: "Remove" })}
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {controller.activeGroupRequiresPostApproval ? (
        <p className="groups-action-hint">
          {t("groups_post_requires_approval_hint", {
            defaultValue:
              "Member posts in this group need admin approval before they appear.",
          })}
        </p>
      ) : null}
      {controller.groupPostError ? (
        <p className="groups-action-error">{controller.groupPostError}</p>
      ) : null}
      <div className="groups-form-actions">
        <button
          type="button"
          className="groups-secondary-btn"
          onClick={controller.cancelGroupPostComposer}
          disabled={controller.groupPostSubmitting}
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          className="groups-primary-btn"
          disabled={controller.groupPostSubmitting}
        >
          {controller.groupPostSubmitting ? t("groups_posting") : t("post")}
        </button>
      </div>
    </form>
  ) : null;

  const activeGroupHomeRules = activeGroupHome.groupRules?.trim() || "";

  const pendingSection = controller.isAdmin(activeGroupHome) ? (
    controller.pendingGroupPostsLoading ? (
      <div className="groups-status-card">
        {t("groups_pending_posts_loading", {
          defaultValue: "Loading pending posts...",
        })}
      </div>
    ) : controller.pendingGroupPostsError ? (
      <div className="groups-status-card groups-status-error">
        {controller.pendingGroupPostsError}
      </div>
    ) : controller.pendingGroupPosts.length > 0 ? (
      <div className="groups-post-list">
        <div className="groups-feed-header">
          <div>
            <h3>
              {t("groups_pending_posts_title", {
                defaultValue: "Pending posts",
              })}
            </h3>
            <p className="groups-feed-subtitle">
              {t("groups_pending_posts_subtitle", {
                defaultValue:
                  "Approve or reject member posts before they appear in the feed.",
              })}
            </p>
          </div>
        </div>

        {controller.groupPostModerationError ? (
          <p className="groups-action-error">
            {controller.groupPostModerationError}
          </p>
        ) : null}

        {controller.pendingGroupPosts.map((post) => {
          const author = controller.getUserById(post.userId);
          const avatarUrl = author?.avatar ? `${API_BASE}${author.avatar}` : null;
          const displayName = author?.username || post.username;
          const approveActionKey = `approve:${post.id}`;
          const rejectActionKey = `reject:${post.id}`;
          const isApproving =
            controller.groupPostModerationLoadingKey === approveActionKey;
          const isRejecting =
            controller.groupPostModerationLoadingKey === rejectActionKey;

          return (
            <article key={post.id} className="groups-post-card">
              <div className="groups-post-card-header">
                <div className="groups-member-avatar groups-post-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={displayName} />
                  ) : (
                    <span>{displayName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="groups-post-card-meta">
                  <strong className="groups-member-name">{displayName}</strong>
                  <span className="groups-post-card-date">
                    {formatGroupPostDate(post.createdAt)}
                  </span>
                </div>
              </div>
              {post.caption ? (
                <p className="groups-post-card-caption">{post.caption}</p>
              ) : null}
              <GroupPostMedia post={post} displayName={displayName} t={t} />
              <div className="groups-form-actions">
                <button
                  type="button"
                  className="groups-secondary-btn"
                  onClick={() => controller.handleModerateGroupPost(post.id, "reject")}
                  disabled={Boolean(controller.groupPostModerationLoadingKey)}
                >
                  {isRejecting
                    ? t("groups_rejecting", { defaultValue: "Rejecting..." })
                    : t("reject", { defaultValue: "Reject" })}
                </button>
                <button
                  type="button"
                  className="groups-primary-btn"
                  onClick={() => controller.handleModerateGroupPost(post.id, "approve")}
                  disabled={Boolean(controller.groupPostModerationLoadingKey)}
                >
                  {isApproving
                    ? t("groups_approving", { defaultValue: "Approving..." })
                    : t("approve", { defaultValue: "Approve" })}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    ) : null
  ) : null;

  const feedContent = (
    <>
      {controller.groupPostNotice ? (
        <div className="groups-status-card">{controller.groupPostNotice}</div>
      ) : null}

      {activeGroupHomeRules ? (
        <div className="groups-status-card">
          <strong>
            {t("groups_rules_label", {
              defaultValue: "Group rules",
            })}
          </strong>
          <p className="groups-feed-subtitle" style={{ whiteSpace: "pre-wrap" }}>
            {activeGroupHomeRules}
          </p>
        </div>
      ) : null}

      {pendingSection}

      {controller.groupPostsLoading ? (
        <div className="groups-status-card">{t("groups_posts_loading")}</div>
      ) : controller.groupPostsError ? (
        <div className="groups-status-card groups-status-error">
          {controller.groupPostsError}
        </div>
      ) : controller.groupPosts.length === 0 ? (
        <div className="groups-empty-state groups-feed-empty">
          <h3>{t("groups_posts_empty_title")}</h3>
          <p>{t("groups_posts_empty_subtitle")}</p>
        </div>
      ) : (
        <div className="groups-post-list">
          {controller.groupPosts.map((post) => {
            const author = controller.getUserById(post.userId);
            const avatarUrl = author?.avatar ? `${API_BASE}${author.avatar}` : null;
            const displayName = author?.username || post.username;
            const likes = Array.isArray(post.likes) ? post.likes : [];
            const comments = Array.isArray(post.comments) ? post.comments : [];
            const userHasLiked = Boolean(
              controller.user?.id && likes.includes(controller.user.id)
            );
            const commentsAreOpen =
              controller.openGroupPostCommentsId === post.id;
            const commentDraft = controller.groupPostCommentDrafts[post.id] || "";
            const postInteractionError =
              controller.groupPostInteractionErrorById[post.id] || "";
            const isLikeLoading = controller.groupPostLikeLoadingId === post.id;
            const isCommentSubmitting =
              controller.groupPostCommentSubmittingId === post.id;

            return (
              <article key={post.id} className="groups-post-card">
                <div className="groups-post-card-header">
                  <div className="groups-member-avatar groups-post-avatar">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={displayName} />
                    ) : (
                      <span>{displayName.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="groups-post-card-meta">
                    <strong className="groups-member-name">{displayName}</strong>
                    <span className="groups-post-card-date">
                      {formatGroupPostDate(post.createdAt)}
                    </span>
                  </div>
                </div>
                {post.caption ? (
                  <p className="groups-post-card-caption">{post.caption}</p>
                ) : null}
                <GroupPostMedia post={post} displayName={displayName} t={t} />
                <div className="groups-post-actions">
                  <button
                    type="button"
                    className={`groups-post-action-btn${
                      userHasLiked ? " is-active" : ""
                    }`}
                    onClick={() => controller.handleToggleGroupPostLike(post.id)}
                    disabled={isLikeLoading}
                  >
                    {isLikeLoading
                      ? t("groups_updating")
                      : `${t("like", { defaultValue: "Like" })} (${likes.length})`}
                  </button>
                  <button
                    type="button"
                    className={`groups-post-action-btn${
                      commentsAreOpen ? " is-active" : ""
                    }`}
                    onClick={() => controller.toggleGroupPostComments(post.id)}
                  >
                    {`${t("comment", { defaultValue: "Comment" })} (${comments.length})`}
                  </button>
                </div>
                {postInteractionError ? (
                  <p className="groups-action-error">{postInteractionError}</p>
                ) : null}
                {commentsAreOpen ? (
                  <div className="groups-post-comments">
                    {comments.length > 0 ? (
                      <div className="groups-post-comment-list">
                        {comments.map((comment) => (
                          <article key={comment.id} className="groups-post-comment">
                            <div className="groups-post-comment-header">
                              <strong className="groups-member-name">
                                {comment.username || t("groups_unknown_member")}
                              </strong>
                              <span className="groups-post-comment-date">
                                {formatGroupPostDate(comment.createdAt)}
                              </span>
                            </div>
                            <p className="groups-post-comment-text">{comment.text}</p>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="groups-feed-subtitle">
                        {t("groups_post_comments_empty", {
                          defaultValue:
                            "No comments yet. Start the conversation.",
                        })}
                      </p>
                    )}
                    <form
                      className="groups-post-comment-form"
                      onSubmit={(event) =>
                        controller.handleSubmitGroupPostComment(post.id, event)
                      }
                    >
                      <textarea
                        value={commentDraft}
                        onChange={(event) =>
                          controller.handleGroupPostCommentDraftChange(
                            post.id,
                            event.target.value
                          )
                        }
                        maxLength={2000}
                        placeholder={t("groups_post_comment_placeholder", {
                          defaultValue: "Write a comment...",
                        })}
                        className="groups-post-comment-input"
                      />
                      <div className="groups-form-actions">
                        <button
                          type="submit"
                          className="groups-primary-btn"
                          disabled={isCommentSubmitting}
                        >
                          {isCommentSubmitting
                            ? t("groups_comment_submitting", {
                                defaultValue: "Commenting...",
                              })
                            : t("comment", { defaultValue: "Comment" })}
                        </button>
                      </div>
                    </form>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <GroupHomeView
      group={activeGroupHome}
      backLabel={t("groups_back_to_list")}
      closeLabel={t("common_close", { defaultValue: "Close" })}
      description={activeGroupHome.description || t("groups_no_description")}
      coverImageUrl={resolveGroupCoverUrl(activeGroupHome.coverImage)}
      coverImageAlt={activeGroupHome.name}
      coverObjectPosition={resolveGroupCoverObjectPosition(activeGroupHome)}
      coverScale={getGroupCoverScale(activeGroupHome)}
      coverInteractive={controller.canOpenGroupCover(activeGroupHome)}
      headerBadges={
        <GroupStatusBadges group={activeGroupHome} controller={controller} t={t} />
      }
      stats={
        <GroupOverviewStats group={activeGroupHome} controller={controller} t={t} />
      }
      actions={
        <GroupActionButtons
          group={activeGroupHome}
          controller={controller}
          t={t}
          detailMode
          options={{ hideOpenGroup: true }}
        />
      }
      invitePanel={
        <GroupInvitePanel group={activeGroupHome} controller={controller} t={t} />
      }
      postToggleLabel={
        controller.isGroupPostComposerOpen
          ? t("groups_hide_post_form")
          : t("groups_create_post")
      }
      feedTitle={t("groups_feed_title")}
      feedSubtitle={t("groups_feed_subtitle")}
      composer={composer}
      feedContent={feedContent}
      onBack={controller.closeGroupHome}
      onCoverClick={() => controller.handleOpenGroupCover(activeGroupHome)}
      onToggleComposer={controller.toggleGroupPostComposer}
    />
  );
}
